/**
 * R 域（重大资产）**端侧文案** —— 中文名与提示语的唯一出处
 *
 * 依据：`R域PRD` v1.3 §5（页面结构）· §6（38 AC）· 原型 `16-重大资产_原型.html` ·
 * `DR-053` D1–D22。
 *
 * ## 🔴 为什么文案单独一个文件
 *
 * 1. **分类/类型的中文名不许出现在 `server` / `shared` 字面量里**（铁律 103）——
 *    默认文案归端侧；`shared` 只给枚举值。
 * 2. 本域有大量"口径提示"（不计收支 / 全额成本 / 禁插值）—— 这些话**必须原样来自
 *    `DR-053`**，散在六个对话框里改一处漏五处。
 *
 * ## ⚠️ 本文件**无逻辑**
 *
 * 只有字符串常量与 `Record` 映射，不导函数 —— 任何"按状态挑文案"的判断都在
 * `features/major-asset.ts` 里，便于单测钉住。
 */

import { DepMethod, MajorAssetType } from '@jiaying/shared';

// ─────────────────────────── 类型 / 折旧 ───────────────────────────

/** 三个类型的中文名（🔴 **不新增第 4 类** · `AC-R1.1`） */
export const TYPE_LABEL: Record<MajorAssetType, string> = {
  [MajorAssetType.Property]: '房产',
  [MajorAssetType.Vehicle]: '车辆',
  [MajorAssetType.Collectible]: '收藏 · 实物',
};

/** 列表分组顺序（固定：房 → 车 → 收藏；空组不渲染） */
export const TYPE_ORDER: readonly MajorAssetType[] = [
  MajorAssetType.Property,
  MajorAssetType.Vehicle,
  MajorAssetType.Collectible,
];

/** 分组副标题（原型 `16-重大资产_原型.html:169` 同句式） */
export const TYPE_HINT: Record<MajorAssetType, string> = {
  [MajorAssetType.Property]: '自住 / 出租 / 待售 —— 全额计入净资产，不减房贷',
  [MajorAssetType.Vehicle]: '按估值全额计入，折旧只做展示',
  [MajorAssetType.Collectible]: '金条 / 字画 / 邮票等 —— 与手动资产不重复登记',
};

export const DEP_LABEL: Record<DepMethod, string> = {
  [DepMethod.Straight]: '直线法',
  [DepMethod.None]: '不折旧',
};

// ─────────────────────────── 列表 / 行 ───────────────────────────

/** 估值缺失时的行内文本（`AC-R2.1` / `AC-R5.3`：**不补 0**） */
export const NO_VALUATION_TEXT = '未填估值';
/** 未估值的回报率格（`AC-R7.4`） */
export const PENDING_VALUATION_TEXT = '待估值';
/** 已处置的行不显示回报率，只显示最终回报 */
export const DISPOSED_STATE_TEXT = '已处置';
export const HOLDING_STATE_TEXT = '持有中';

export const LOAN_NONE_TEXT = '无';
export const LOAN_COUNT_SUFFIX = ' 笔';

export const LIST_TITLE = '重大资产';
export const LIST_SUBTITLE = '台账 + 资金动作 + 回报 · 购入 → 持有 → 处置全生命周期';
export const LIST_ADD = '添加重大资产';

export const HIDDEN_SECTION_TITLE = '已隐藏';
export const HIDDEN_SECTION_HINT = '不计入净资产 · 可随时恢复';
export const DISPOSED_SECTION_TITLE = '已处置';
export const DISPOSED_SECTION_HINT = '回报已收口 · 只读历史';

export const EMPTY_LIST_TEXT = '还没有重大资产。房产 / 车辆 / 收藏都可建 —— 建完就能记购入、调估值、看回报。';
export const EMPTY_VALUATION_TEXT = '还没有估值记录 —— 点「调整估值」写下第一次';

// ─────────────────────────── 详情 ───────────────────────────

export const DETAIL_RETURN_TITLE = '回报面板';
export const DETAIL_RETURN_HINT = '分母 = 累计投入（全额成本 · 含贷款部分）· 提前还贷不改变回报率';
export const DETAIL_CURVE_TITLE = '估值曲线';
export const DETAIL_CURVE_HINT = '只画真实点 · 禁插值 / 平推 / 估算';
export const DETAIL_HISTORY_TITLE = '估值历史';
export const DETAIL_HISTORY_HINT = '每次调整留痕 · 可回看';
export const DETAIL_LOAN_TITLE = '关联房贷';
export const DETAIL_DEP_TITLE = '折旧 · 账面净值';

export const RETURN_INVESTED = '累计投入';
export const RETURN_INCOME = '累计收益';
export const RETURN_PNL = '账面盈亏';
export const RETURN_ROI = '回报率';
export const RETURN_FINAL_PNL = '最终回报';
export const RETURN_PROCEEDS = '处置实得';

/** 口径说明（🔴 与 `AC-R7.5` / `AC-R2.3` 的原文一致，不改口径） */
export const NET_EQUITY_HINT_PREFIX = '净权益提示 = 估值 − 关联房贷剩余 = ';
export const NET_EQUITY_HINT_SUFFIX = '（仅提示：资产侧不扣减 —— 负债侧已单列，双扣即错）';
export const BOOK_VALUE_HINT = '账面净值 = 购置成本 − 累计折旧；不进净资产、不进回报（净资产按当前估值全额计入）';
export const CURVE_EMPTY_HINT = '曲线只用调整估值时写入的真实记录 —— 不估算、不平推、不插值';
export const CURVE_SINGLE_HINT = '只有 1 个估值点：只画点，两点才会连成线';

// ─────────────────────────── 六个对话框 ───────────────────────────

export const DLG_ASSET_TITLE_CREATE = '添加重大资产';
export const DLG_ASSET_TITLE_EDIT = '编辑重大资产';
export const DLG_VALUATION_TITLE = '调整估值';
export const DLG_PURCHASE_TITLE = '购入';
export const DLG_APPEND_TITLE = '追加投资';
export const DLG_INCOME_TITLE = '记收益';
export const DLG_DISPOSE_TITLE = '出售 / 处置';
export const DLG_LOANS_TITLE = '管理关联贷款';

/** 台账表单 */
export const F_TYPE = '类型';
export const F_NAME = '名称';
export const F_OWNER = '归属人';
export const F_PURCHASE_DATE = '购置日';
export const F_CURRENCY = '币种';
export const F_PURCHASE_COST = '购置成本';
export const F_ESTIMATED = '当前估值';
export const F_DEP_METHOD = '折旧方法';
export const F_DEP_YEARS = '折旧年限';
export const F_LOCATION = '位置';
export const F_AREA = '面积（㎡）';
export const F_NOTE = '备注';
export const F_HIDDEN = '隐藏（不计入净资产，可随时恢复）';

export const HINT_ASSET_MIN = '必填仅 类型 / 名称 / 归属人，其余可空可后补（`AC-R1.2`）';
export const HINT_ASSET_ESTIMATED = '估值可空 —— 空着就是「未填估值」，不会被当成 0';

/** 购入 */
export const F_TOTAL = '购入总价';
export const F_DATE = '日期';
export const F_DOWN_PAYMENT = '首付';
export const F_FROM_ACCOUNT = '首付付款账户';
export const F_LOAN_AMOUNT_HINT = '贷款金额 = 总价 − 首付（服务端校验并生成两腿）';
export const F_LOAN_ACCOUNT = '贷款账户';
export const F_NEW_LOAN = '新建贷款账户';
export const F_ANNUAL_RATE = '年利率（%）';
export const F_TERMS = '期数';
export const F_START_DATE = '首期日';
export const F_DUE_DATE = '到期日';
export const F_METHOD = '还款方式';
export const HINT_PURCHASE = '购入不计收支（购置现金流 ≠ 支出）· 贷款买入时首付 + 贷款本金 = 同一笔交易，禁拆两笔';

/** 追加 / 收益 */
export const F_AMOUNT = '金额';
export const F_TO_ACCOUNT = '收款账户';
export const F_CATEGORY = '分类（二选一）';
export const HINT_APPEND = '不计收支 · 计入累计投入（回报分母变大）· 币种须与资产一致';
export const HINT_INCOME = '计收入（进收支报表）· 房屋出租 → 租金收入；其余资产收益 → 投资收益 · 不新增分类';

/** 调估值 */
export const F_NEW_VALUE = '新估值';
export const F_VALUATION_DATE = '估值日';
export const HINT_VALUATION = '留痕：本次估值写入历史（估值曲线即时进点）· 不产生任何交易现金流';

/** 处置 */
export const F_PROCEEDS = '实得价（0 = 报废）';
export const F_SETTLE_LOANS = '一并结清关联贷款（调用「提前结清」· 后续期次作废、历史不动）';
export const HINT_DISPOSE = '不计收支：卖房款是资产变现、不是收入（与购入对称）· 处置后关联解除、退出净资产、回报收口';

/** 关联贷款 */
export const HINT_LOANS = '只能关联贷款类账户（服务端校验，不止 UI 过滤）· 重复关联幂等';

/** 按钮 */
export const BTN_SAVE = '保存';
export const BTN_CANCEL = '取消';
export const BTN_PURCHASE = '购入';
export const BTN_APPEND = '记追加';
export const BTN_INCOME = '记收益';
export const BTN_SAVE_VALUATION = '保存估值';
export const BTN_DISPOSE = '确认处置';
export const BTN_DELETE = '删除';
export const BTN_EDIT = '编辑';
export const BTN_ADJUST = '调整估值';

/** 收益分类（端侧文案；`shared` 只给 id）*/
export const INCOME_CATEGORY_LABEL: Record<string, string> = {
  'cat-rent-income': '租金收入',
  'cat-invest-gain': '投资收益',
};
